import { SendHorizontal } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { useSession } from '@/session/store';
import { MicButton } from './MicButton';

const MAX_ROWS_PX = 160;

export function Composer() {
  const connection = useSession((state) => state.connection);
  const streaming = useSession((state) => state.streaming);
  const micArmed = useSession((state) => state.micArmed);
  const send = useSession((state) => state.send);
  const dismissIntro = useSession((state) => state.dismissIntro);
  const reconnect = useSession((state) => state.reconnect);

  const [draft, setDraft] = useState('');
  const input = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const node = input.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, MAX_ROWS_PX)}px`;
  }, [draft]);

  const offline = connection !== 'open';
  // A streaming reply is not a reason to lock the composer: sending over one is barge-in, and it is
  // the only way to correct an answer that has gone the wrong way without waiting it out.
  const busy = offline || micArmed;

  function submit() {
    const text = draft.trim();
    if (!text || busy) return;
    send(text);
    setDraft('');
  }

  return (
    <footer className="border-line-soft from-shell shrink-0 border-t bg-gradient-to-t to-transparent px-4 pt-3 pb-5 sm:px-6">
      <div className="mx-auto w-full max-w-3xl">
        {offline ? (
          <button
            type="button"
            onClick={reconnect}
            disabled={connection === 'connecting'}
            className="border-line bg-surface hover:border-accent/50 w-full rounded-2xl border py-3 text-sm font-medium transition disabled:opacity-50"
          >
            {connection === 'connecting' ? 'Connecting…' : 'Reconnect and start a new session'}
          </button>
        ) : (
          <div
            className={cn(
              'border-line bg-surface/85 flex items-end gap-2 rounded-panel border p-2 shadow-2xl backdrop-blur transition',
              'focus-within:border-accent/45',
              micArmed && 'border-accent/60',
            )}
          >
            <MicButton />

            <textarea
              ref={input}
              rows={1}
              value={draft}
              disabled={micArmed}
              placeholder={
                micArmed
                  ? 'Listening…'
                  : streaming
                    ? 'Type to interrupt — or hold space to speak over it'
                    : 'Ask for something to watch — or hold space to speak'
              }
              onChange={(event) => {
                setDraft(event.target.value);
                // The first character retires the opening prompt. Emptying the field again does not
                // bring it back: the user has already answered it.
                if (event.target.value) dismissIntro();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submit();
                }
              }}
              className="scroll-slim placeholder:text-faint max-h-40 flex-1 resize-none bg-transparent px-1.5 py-2.5 text-[15px] leading-relaxed outline-none disabled:opacity-60"
            />

            <button
              type="button"
              aria-label="Send"
              onClick={submit}
              disabled={busy || !draft.trim()}
              className="bg-accent grid size-11 shrink-0 place-items-center rounded-full text-white transition hover:brightness-110 disabled:pointer-events-none disabled:opacity-30"
            >
              <SendHorizontal className="size-5" />
            </button>
          </div>
        )}
      </div>
    </footer>
  );
}
